import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Compass, Brain } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import ProgramSummary from '../components/ProgramSummary';
import OptionsSummary from '../components/OptionsSummary';
import ContactForm from '../components/ContactForm';
import PricingSummary from '../components/PricingSummary';

const ContactFormPage = () => {
  const navigate = useNavigate();
  const [programData, setProgramData] = useState(null);
  const [optionsData, setOptionsData] = useState(null);
  const [currentStep] = useState(3);
  const [contactData, setContactData] = useState({
    prenom: '',
    nom: '',
    fonction: '',
    entreprise: '',
    telephone: '',
    email: '',
    contact_preference: 'rappel'
  });
  const [pricing, setPricing] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Redirection automatique vers la page de confirmation
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        window.location.href = 'https://executivelearning.fr/confirmation-devis/';
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  useEffect(() => {
    // Charger les données sauvegardées
    const savedProgram = localStorage.getItem('simulator_program');
    const savedOptions = localStorage.getItem('simulator_options');
    
    if (!savedProgram || !savedOptions) {
      navigate('/');
      return;
    }

    const program = JSON.parse(savedProgram);
    const options = JSON.parse(savedOptions);
    
    setProgramData(program);
    setOptionsData(options);

    // Calculer le prix
    calculatePrice(program, options);
    
    // Auto-scroll vers le titre Executive Learning après un délai
    setTimeout(() => {
      const titleSection = document.getElementById('executive-title');
      if (titleSection) {
        titleSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 800);
  }, [navigate]);

  const calculatePrice = async (program, options) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calculate-price`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ program, options })
      });
      
      const result = await response.json();
      if (result.success) {
        setPricing(result.data);
      }
    } catch (error) {
      console.error('Erreur calcul prix:', error);
    }
  };

  const handleContactChange = (field, value) => {
    setContactData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    const requiredFields = ['prenom', 'nom', 'fonction', 'entreprise', 'telephone', 'email'];
    const missingFields = requiredFields.filter(field => !contactData[field].trim());
    
    if (missingFields.length > 0) {
      console.log('Champs manquants:', missingFields);
      return;
    }

    if (!contactData.email.includes('@')) {
      console.log('Email invalide');
      return;
    }

    // Validation téléphone français
    const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
    if (!phoneRegex.test(contactData.telephone.replace(/\s/g, ''))) {
      console.log('Numéro de téléphone français invalide');
      return;
    }

    setIsSubmitting(true);

    try {
      // Préparer les données pour l'envoi SANS capture d'écran
      const simulatorData = {
        program: { groups: programData.groups },
        options: optionsData,
        contact: contactData
      };

      // Envoyer le devis SANS capture d'écran
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          simulator_data: simulatorData
          // Plus de screenshot_base64
        })
      });

      const result = await response.json();
      if (result.success) {
        setShowSuccess(true);
        // Nettoyer le localStorage
        localStorage.removeItem('simulator_program');
        localStorage.removeItem('simulator_options');
      } else {
        throw new Error(result.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur envoi devis:', error);
      alert('Erreur lors de l\'envoi du devis. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md mx-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">Devis envoyé !</h2>
          <p className="text-gray-600 mb-6 text-center">
            Pensez à regarder vos spams
          </p>
          <div className="text-center">
            <a
              href="https://executivelearning.fr/confirmation-devis/"
              className="btn-primary inline-block"
            >
              Executive:Learning
            </a>
            <p className="text-sm text-gray-500 mt-4">
              Redirection automatique dans 3 secondes...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!programData || !optionsData) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Titre principal */}
      <div className="bg-[#fff5ee] py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-center mb-8">Construisez votre programme de formation</h1>
          <Breadcrumb currentStep={currentStep} />
        </div>
      </div>
      
      {/* Récapitulatifs */}
      <div className="summary-container" id="summary-section">
        <div className="summary-content">
          <ProgramSummary 
            programData={programData}
            showEditButton={true}
            editButtonText="Modifier le programme"
            editRoute="/"
          />
          <OptionsSummary 
            optionsData={optionsData}
            showEditButton={true}
            editButtonText="Modifier les options"
            editRoute="/options"
          />
        </div>
      </div>

      {/* Présentation Executive Learning */}
      <div className="max-w-6xl mx-auto px-4 mt-100 mb-50">
        <h2 className="text-left mb-50" id="executive-title">Executive:Learning, un programme de<br />formation au management unique</h2>
        
        {/* 3 colonnes de présentation */}
        <div className="grid grid-cols-3 gap-8 mb-100">
          <div className="text-center">
            <Eye className="w-12 h-12 mx-auto mb-4 text-[#fa6800]" strokeWidth={2} />
            <h3 className="mb-4">Ancrage terrain</h3>
            <p className="text-base leading-relaxed">
              Nos formations s'appuient sur l'observation directe des apprenants dans leurs activités métiers, 
              pour concevoir des actions parfaitement adaptées à leur réalité opérationnelle.
            </p>
          </div>
          <div className="text-center">
            <Compass className="w-12 h-12 mx-auto mb-4 text-[#fa6800]" strokeWidth={2} />
            <h3 className="mb-4">Pragmatisme</h3>
            <p className="text-base leading-relaxed">
              Chaque outil et exercice est conçu pour être concret, utile et immédiatement déployable, 
              avec des ajustements au plus près du quotidien des équipes.
            </p>
          </div>
          <div className="text-center">
            <Brain className="w-12 h-12 mx-auto mb-4 text-[#fa6800]" strokeWidth={2} />
            <h3 className="mb-4">Pédagogie cognitive</h3>
            <p className="text-base leading-relaxed">
              Nous intégrons les apports de sciences cognitives afin de favoriser un apprentissage durable 
              et l'ancrage réel de nouvelles compétences
            </p>
          </div>
        </div>

        {/* Déroulé d'apprentissage */}
        <p className="text-left mb-50 mt-100">
          Le déroulé d'apprentissage par module se découpe toujours en 3 temps
        </p>
        
        <div className="grid grid-cols-3 gap-8 mb-100">
          <div className="bg-[#fff5ee] p-6 rounded-lg">
            <h3 className="text-[#fa6800] mb-4">La formation<br />théorique</h3>
            <p className="text-base">
              Apprentissage de 3 à 5 compétences clés, traduites dans des exercices pratiques 
              à disposition et des études de cas (fictives ou réelles)
            </p>
          </div>
          <div className="bg-[#FEF9F0] p-6 rounded-lg">
            <h3 className="text-[#f0a30a] mb-4">La journée<br />d'observation</h3>
            <p className="text-base">
              Des observations terrain de l'apprenant en situation de travail (sur 2 à 4 activités métier dans la journée), 
              des feedbacks directs. Une synthèse et un retour d'expérience de l'apprenant en fin de journée 
              et définition des prochains micro objectifs de développement
            </p>
          </div>
          <div className="bg-[#fff5ee] p-6 rounded-lg">
            <h3 className="text-[#fa6800] mb-4">L'ancrage par<br />feedback</h3>
            <p className="text-base">
              Evaluation croisée des objectifs pédagogiques, renforcement des compétences par réappropriation, 
              révision ou simulation (mise en pratique)
            </p>
          </div>
        </div>
      </div>

      {/* Section Budget avec titre global */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <h2 className="text-left mb-8">Votre budget</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Récapitulatif des prix */}
          <div className="h-full">
            {pricing && (
              <PricingSummary
                pricing={pricing}
                programData={programData}
                optionsData={optionsData}
              />
            )}
          </div>

          {/* Formulaire de contact */}
          <div className="h-full">
            <ContactForm
              data={contactData}
              onChange={handleContactChange}
            />
          </div>
        </div>

        {/* Bouton envoi */}
        <div className="text-center mt-12">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`btn-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="send-quote-button"
          >
            {isSubmitting ? 'Envoi en cours...' : 'Valider'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactFormPage;